import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { uploadFile } from 'zite-file-upload-sdk';
import { toast } from 'sonner';
import {
  Plus, Trash2, Upload, Globe, File, Loader2, X, ExternalLink,
} from 'lucide-react';

export type ResourceLink = { label: string; url: string };
export type ResourceFile = { url: string; name: string };

interface Props {
  links: ResourceLink[];
  files: ResourceFile[];
  onLinksChange: (links: ResourceLink[]) => void;
  onFilesChange: (files: ResourceFile[]) => void;
}

export default function ComplianceResourceEditor({ links, files, onLinksChange, onFilesChange }: Props) {
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addLink = () => {
    if (!newUrl.trim()) { toast.error('URL is required'); return; }
    const url = newUrl.trim().startsWith('http') ? newUrl.trim() : `https://${newUrl.trim()}`;
    onLinksChange([...links, { label: newLabel.trim() || url, url }]);
    setNewLabel('');
    setNewUrl('');
  };

  const removeLink = (i: number) => onLinksChange(links.filter((_, idx) => idx !== i));

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File must be under 25MB'); return; }
    setUploading(true);
    try {
      const { fileUrl } = await uploadFile({ data: file, filename: file.name });
      onFilesChange([...files, { url: fileUrl, name: file.name }]);
      toast.success(`${file.name} uploaded`);
    } catch {
      toast.error('Upload failed — please try again');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = (i: number) => onFilesChange(files.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      {/* Web Links */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-3.5 h-3.5 text-primary" />
          <p className="text-xs font-semibold text-foreground uppercase tracking-wider">Web Links</p>
        </div>

        {links.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {links.map((link, i) => (
              <div key={i} className="flex items-center gap-2 bg-muted/40 rounded px-2.5 py-1.5 text-xs">
                <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate text-foreground font-medium">{link.label}</span>
                <span className="text-muted-foreground truncate max-w-[160px] hidden sm:block">{link.url}</span>
                <button onClick={() => removeLink(i)} className="text-muted-foreground hover:text-destructive ml-1 shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-5 gap-2">
          <Input
            className="col-span-2 h-8 text-xs"
            placeholder="Label (e.g. Food Safety Guide)"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
          />
          <Input
            className="col-span-2 h-8 text-xs"
            placeholder="https://example.com/guide"
            value={newUrl}
            onChange={e => setNewUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addLink()}
          />
          <Button size="sm" variant="outline" className="h-8" onClick={addLink}>
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">
          Links will be shown to employees as study resources and fetched for AI quiz generation.
        </p>
      </div>

      {/* Uploaded Files */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <File className="w-3.5 h-3.5 text-primary" />
          <p className="text-xs font-semibold text-foreground uppercase tracking-wider">Uploaded Documents</p>
        </div>

        {files.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 bg-muted/40 rounded px-2.5 py-1.5 text-xs">
                <File className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate text-foreground">{f.name}</span>
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline shrink-0">
                  View
                </a>
                <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-destructive ml-1 shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.png,.jpg" onChange={handleFileUpload} />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
          {uploading ? 'Uploading...' : 'Upload File'}
        </Button>
        <p className="text-xs text-muted-foreground mt-1.5">
          PDF, Word, or image files up to 25MB. Employees can download these as study materials.
        </p>
      </div>
    </div>
  );
}
